import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import useTranslation from '../hooks/useTranslation';
import Card from './Card';
import { getApiBaseUrl } from '../../core/config/api';

interface ServerStatusInfo {
  isOnline: boolean;
  isMaintenance: boolean;
  responseTime: number;
  lastCheck: Date;
  error?: string;
}

const ServerStatus: React.FC = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [status, setStatus] = useState<ServerStatusInfo>({
    isOnline: false,
    isMaintenance: false,
    responseTime: 0,
    lastCheck: new Date()
  });

  const checkServerStatus = useCallback(async () => {
    try {
      const startTime = Date.now();
      
      // Utiliser l'API config pour obtenir l'URL de base
      const baseUrl = getApiBaseUrl(true).replace(/\/$/, '');
      const possibleUrls = process.env.NODE_ENV === 'production'
        ? [`${baseUrl}/health`]
        : [
            'http://localhost:3001/',
            'http://localhost:3000/',
            'http://localhost:8000/',
            'http://127.0.0.1:3001/',
            'http://127.0.0.1:3000/',
            'http://127.0.0.1:8000/'
          ];
      
      let serverFound = false;
      let responseTime = 0;
      
      // Tester chaque URL jusqu'à en trouver une qui fonctionne
      for (const url of possibleUrls) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 2000);
          
          const response = await fetch(url, {
            method: 'GET',
            signal: controller.signal,
          });

          clearTimeout(timeoutId);
          if (!response.ok) {
            continue;
          }
          responseTime = Date.now() - startTime;
          serverFound = true;
          break;
        } catch (err) {
          continue;
        }
      }
      
      if (serverFound) {
        // Vérifier aussi le statut de maintenance (si la route existe)
        try {
          const maintenanceController = new AbortController();
          const maintenanceTimeoutId = setTimeout(() => maintenanceController.abort(), 2000);
          
          const maintenanceResponse = await fetch(`${baseUrl}/admin/maintenance/status`, {
            signal: maintenanceController.signal
          });
          
          clearTimeout(maintenanceTimeoutId);
          if (maintenanceResponse.ok) {
            const data = await maintenanceResponse.json();
            const isMaintenance = data.success && data.data && (data.data.enabled === 1 || data.data.enabled === true);
            
            setStatus({
              isOnline: true,
              isMaintenance,
              responseTime,
              lastCheck: new Date(),
              error: undefined
            });
            return;
          }
        } catch (err: any) {
          // Si la vérification de maintenance échoue (route n'existe pas ou erreur réseau),
          // on ignore silencieusement et on considère que le serveur est en ligne
          // Ne pas logger les erreurs 404 car la route peut ne pas exister
          if (err.name !== 'AbortError' && !err.message?.includes('404')) {
            // Log seulement les autres erreurs non-404 en mode debug
            console.debug('Maintenance status check failed:', err);
          }
        }
        
        setStatus({
          isOnline: true,
          isMaintenance: false,
          responseTime,
          lastCheck: new Date(),
          error: undefined
        });
      } else {
        throw new Error('Aucun serveur trouvé sur les ports testés');
      }
      
    } catch (error) {
      let errorMessage = t('serverStatus.error') || 'Serveur non accessible';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = t('serverStatus.timeout') || 'Timeout de connexion';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = t('serverStatus.offline') || 'Serveur non démarré';
        } else {
          errorMessage = error.message;
        }
      }
      
      setStatus({
        isOnline: false,
        isMaintenance: false,
        responseTime: 0,
        lastCheck: new Date(),
        error: errorMessage
      });
    }
  }, [t]);

  useEffect(() => {
    checkServerStatus();
    
    // Vérifier toutes les 30 secondes
    const interval = setInterval(checkServerStatus, 30000);
    
    // Vérifier quand la page redevient visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkServerStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkServerStatus]);

  const getStatusColor = () => {
    if (!status.isOnline) return 'bg-red-500';
    if (status.isMaintenance) return 'bg-orange-500';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    if (!status.isOnline) return t('serverStatus.offline') || 'Serveur hors ligne';
    if (status.isMaintenance) return t('serverStatus.maintenance') || 'Maintenance en cours';
    return t('serverStatus.online') || 'Serveur opérationnel';
  };

  return (
    <Card className="p-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-white mb-4">
          {t('serverStatus.title') || 'Statut du Serveur'}
        </h3>
        
        {/* Indicateur de statut */}
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className={`w-4 h-4 rounded-full ${getStatusColor()} animate-pulse`}></div>
          <span className="text-white font-semibold text-lg">{getStatusText()}</span>
        </div>
        
        {/* Informations supplémentaires */}
        {status.isOnline && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <p className="text-gray-400 text-sm">
              {t('serverStatus.responseTime') || 'Temps de réponse'}: {status.responseTime}ms
            </p>
            {status.error && (
              <p className="text-red-400 text-sm mt-2">{status.error}</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ServerStatus;

